<script>
import axios from 'axios'
import { logout, can, refreshUser, getUser, pinStatus } from '@/auth'
import { isRouteAllowed, firstAllowedPath } from '@/router'
import { formatAgo } from '@/zones'
import { startLive, stopLive, liveState } from '@/live'
import { versionState, reloadNow } from '@/version'
import { lockState, lockNow, openSwitch, startIdleWatch, stopIdleWatch } from '@/lock'
import { syncPush, reportInstalled } from '@/push'
import { pwaState, installApp } from '@/pwa'
import LockScreen from '@/components/LockScreen.vue'
import PushPrompt from '@/components/PushPrompt.vue'

// Страницы, где данные идут из Fort Monitor: на них показывается предупреждение об устаревших данных.
const FORT_PAGES = ['/zones', '/fuel', '/wash', '/shift-change']
// Как часто проверять свежесть данных Fort Monitor, пока открыта такая страница.
const SYNC_CHECK_MS = 60 * 1000
// Как часто подтягивать права: администратор мог включить или отключить пользователю страницы.
const ACCESS_CHECK_MS = 2 * 60 * 1000

export default {
  name: 'AppShell',
  components: { LockScreen, PushPrompt },
  data() {
    return {
      openDropdown: null,
      menuOpen: false,
      profileOpen: false,
      // сотрудники, под которыми можно войти по ПИН-коду (отмечены «быстрый выбор»)
      switchUsers: [],
      sync: null,
      syncTimer: null,
      accessTimer: null,
      // perm — страница, которая должна быть включена пользователю (экран «Пользователи»)
      nav: [
        { label: 'Разнарядка', to: '/schedule/day', perm: 'page.schedule' },
        {
          label: 'Заказы',
          children: [
            { label: 'Список заказов', to: '/orders/list', perm: 'page.orders' },
            { label: 'Добавить заказ', to: '/orders/add', perm: 'page.orders_add' }
          ]
        },
        {
          label: 'Ремонты',
          children: [
            { label: 'Ремонтные листы', to: '/repairs/list', perm: 'page.repairs' },
            { label: 'График ремонтов', to: '/repairs/gantt', perm: 'page.repairs' },
            { label: 'Заявка на ремонт', to: '/repairs/add', perm: 'page.repairs_add' }
          ]
        },
        { label: 'Обращения', to: '/driver-appeals', perm: 'page.appeals' },
        { label: 'Кто где сейчас', to: '/zones/now', perm: 'page.zones_now' },
        { label: 'Пересменка', to: '/shift-change', perm: 'page.shift_change' },
        { label: 'Оплаты мехслужбы', to: '/finance', perm: 'page.finance' },
        { label: 'Оплаты', to: '/payments', perm: 'page.payments' },
        {
          label: 'Отчёты',
          children: [
            { label: 'По моделям 1', to: '/print/car', perm: 'page.report_car' },
            { label: 'По партиям', to: '/print/route', perm: 'page.report_route' },
            { label: 'По моделям 2', to: '/report/models', perm: 'page.report_models' },
            { label: 'По водителям', to: '/report/drivers', perm: 'page.report_drivers' },
            { label: 'Аналитика ремонтов', to: '/report/repairs', perm: 'page.repair_analytics' },
            { label: 'Спецодежда', to: '/report/workwear', perm: 'page.report_workwear' },
            { label: 'Статистика заездов', to: '/zones/stats', perm: 'page.zones_stats' },
            { label: 'Топливо', to: '/fuel', perm: 'page.fuel' },
            { label: 'Автомойка', to: '/wash', perm: 'page.wash' }
          ]
        },
        { label: 'Журнал', to: '/audit', perm: 'page.audit' },
        {
          label: 'Справочники',
          children: [
            { label: 'Партии', to: '/batches', perm: 'page.batches' },
            { label: 'Места', to: '/zones/places', perm: 'page.zones_places' },
            { label: 'Маршруты', to: '/routes/list', perm: 'page.routes' },
            { label: 'Добавить маршрут', to: '/routes/add', perm: 'page.routes_add' },
            { label: 'Расчёт плеча', to: '/routes/shoulder', perm: 'page.shoulder' },
            { label: 'Транспортные средства', to: '/vehicles', perm: 'page.vehicles' },
            { label: 'Водители', to: '/drivers', perm: 'page.drivers' },
            { label: 'Поставщики', to: '/finance/suppliers', perm: 'page.finance' },
            { label: 'Номенклатура', to: '/finance/items', perm: 'page.finance' },
            { label: 'Пользователи', to: '/users', perm: 'page.users' },
            { label: 'Синхронизации', to: '/syncs', perm: 'page.syncs' }
          ]
        }
      ]
    }
  },
  computed: {
    // Меню только из включённых пользователю страниц; раздел без доступных страниц скрывается.
    visibleNav() {
      return this.nav
        .map((item) => (item.children ? Object.assign({}, item, { children: item.children.filter((c) => can(c.perm)) }) : item))
        .filter((item) => (item.children ? item.children.length > 0 : can(item.perm)))
    },
    // Имя и логин текущего пользователя из базы (приходят при входе и обновляются вместе с правами).
    currentUser() {
      return getUser() || {}
    },
    // состояние приложения (PWA): можно ли установить
    pwa() {
      return pwaState
    },
    live() {
      return liveState
    },
    // Заставка по простою, блокировка экрана или выбор сотрудника
    lockShown() {
      return lockState.locked || lockState.switching || lockState.saver
    },
    // Блокировать экран можно, когда есть ПИН-код, которым его потом открыть
    canLock() {
      return !!this.currentUser.has_pin
    },
    version() {
      return versionState
    },
    userName() {
      return this.currentUser.name || this.currentUser.username || ''
    },
    // В шапке — только имя («Фамилия Имя Отчество» → «Имя»); полное ФИО — в окне профиля
    userFirstName() {
      const parts = this.userName.trim().split(/\s+/)
      return parts.length > 1 ? parts[1] : parts[0] || ''
    },
    userInitial() {
      const name = this.userName.trim()
      return name ? name.charAt(0).toUpperCase() : '?'
    },
    onZonesPages() {
      return FORT_PAGES.some((prefix) => this.$route.path.indexOf(prefix) === 0)
    },
    // Данные Fort Monitor устарели: последний успешный цикл слишком давно или его не было.
    syncStale() {
      return this.onZonesPages && !!this.sync && this.sync.stale
    },
    syncText() {
      if (!this.sync || !this.sync.synced_at) return 'Данных от Fort Monitor нет'
      return 'Данные устарели: ' + formatAgo(this.sync.synced_at)
    },
    syncTitle() {
      const s = this.sync
      if (!s) return ''
      const lines = ['Fort Monitor не обновлял данные дольше ' + Math.round(s.stale_after_seconds / 60) + ' мин.']
      if (s.last_run && !s.last_run.ok) {
        lines.push('Последняя попытка не удалась: ' + (s.last_run.error || 'неизвестная ошибка') + '.')
        if (s.failures_in_row > 1) lines.push('Неудач подряд: ' + s.failures_in_row + '.')
      } else if (!s.last_run) {
        lines.push('Попыток синхронизации ещё не было.')
      } else {
        lines.push('Синхронизация не запускается: проверьте планировщик на сервере.')
      }
      return lines.join(' ')
    }
  },
  watch: {
    // приложение только что установлено на устройство (браузер сообщил appinstalled): запись в журнал
    'pwa.standalone'(on) {
      if (on) reportInstalled()
    },
    // Следим за состоянием синхронизации только на страницах с данными Fort Monitor.
    '$route.path'() {
      this.watchSync()
      this.menuOpen = false
      this.openDropdown = null
    }
  },
  mounted() {
    // Вебсокеты: страницы обновляются сами, когда данные меняются у других пользователей и после синхронизаций
    startLive()
    document.addEventListener('click', this.handleOutsideClick)
    window.addEventListener('focus', this.checkAccess)
    this.watchSync()
    this.checkAccess()
    this.accessTimer = setInterval(this.checkAccess, ACCESS_CHECK_MS)
    // Автоблокировка экрана после пяти минут без действий
    startIdleWatch()
    // Если этот браузер уже подписан на push, привязываем его к вошедшему пользователю
    syncPush()
  },
  beforeDestroy() {
    stopLive()
    stopIdleWatch()
    clearInterval(this.syncTimer)
    clearInterval(this.accessTimer)
    window.removeEventListener('focus', this.checkAccess)
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    reloadNow,
    installApp,
    // Подтягивает актуальные права; если открытая страница больше не включена — переходит на доступную.
    checkAccess() {
      refreshUser().then(() => {
        // со страницы «Нет доступа» уходим, как только пользователю включили хоть одну страницу
        if (!isRouteAllowed(this.$route) || this.$route.path === '/forbidden') {
          const fallback = firstAllowedPath()
          if (fallback !== this.$route.path) this.$router.replace(fallback)
        }
      })
    },
    watchSync() {
      clearInterval(this.syncTimer)
      this.syncTimer = null
      if (!this.onZonesPages) return
      this.loadSync()
      this.syncTimer = setInterval(this.loadSync, SYNC_CHECK_MS)
    },
    loadSync() {
      axios.get('/sync/status')
        .then((res) => {
          this.sync = res.data || null
        })
        .catch(() => {
          // метка вспомогательная: если статус недоступен, просто не показываем её
        })
    },
    handleOutsideClick(e) {
      if (!this.$el.contains(e.target)) {
        this.openDropdown = null
        this.profileOpen = false
        this.menuOpen = false
      }
    },
    toggleDropdown(label) {
      this.openDropdown = this.openDropdown === label ? null : label
      this.profileOpen = false
    },
    toggleProfile() {
      this.profileOpen = !this.profileOpen
      if (this.profileOpen) this.loadSwitchUsers()
      this.openDropdown = null
      this.menuOpen = false
    },
    isActive(item) {
      if (item.to) return this.$route.path === item.to
      if (item.children) return item.children.some(c => this.$route.path === c.to)
      return false
    },
    go(to) {
      this.openDropdown = null
      this.menuOpen = false
      if (this.$route.path !== to) this.$router.push(to)
    },
    doLock() {
      this.profileOpen = false
      lockNow()
    },
    loadSwitchUsers() {
      pinStatus(true)
        .then((data) => {
          this.switchUsers = (Array.isArray(data.quick_users) ? data.quick_users : []).filter((u) => u.id !== this.currentUser.id)
        })
        .catch(() => {
          // без списка остаётся блокировка экрана и вход любым ПИН-кодом
        })
    },
    // Выбран сотрудник: открывается ввод его ПИН-кода
    openProfile() {
      this.profileOpen = false
      if (this.$route.path !== '/profile') this.$router.push('/profile')
    },
    installFromMenu() {
      this.profileOpen = false
      installApp()
    },
    doSwitchUser(user) {
      this.profileOpen = false
      openSwitch(user)
    },
    doLogout() {
      this.profileOpen = false
      logout()
      this.$router.push('/login')
    }
  }
}
</script>

<template>
  <div class="app-shell">
    <header class="shell-header">
      <router-link to="/" class="shell-logo">
        <img src="/strnt_logo.png" alt="СпецТехРесурс НТ" />
      </router-link>

      <button
        type="button"
        class="shell-burger"
        :class="{ 'is-open': menuOpen }"
        aria-label="Меню"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        @click.stop="menuOpen = !menuOpen"
      >
        <i class="fas" :class="menuOpen ? 'fa-times' : 'fa-bars'"></i>
      </button>

      <nav class="shell-nav" :class="{ 'is-open': menuOpen }">
        <div
          v-for="item in visibleNav"
          :key="item.label"
          class="shell-nav-item"
          :class="{ 'is-open': openDropdown === item.label }"
        >
          <router-link
            v-if="item.to"
            :to="item.to"
            class="shell-nav-link"
            :class="{ 'is-active': isActive(item) }"
          >
            {{ item.label }}
          </router-link>

          <button
            v-else
            type="button"
            class="shell-nav-link shell-nav-dropdown-trigger"
            :class="{ 'is-active': isActive(item) }"
            @click.stop="toggleDropdown(item.label)"
          >
            {{ item.label }}
            <i class="fas fa-chevron-down shell-caret"></i>
          </button>

          <transition name="dd">
            <div v-if="item.children" v-show="openDropdown === item.label" class="shell-dropdown">
              <a
                v-for="child in item.children"
                :key="child.label"
                class="shell-dropdown-link"
                :class="{ 'is-active': $route.path === child.to }"
                @click.stop="go(child.to)"
              >
                {{ child.label }}
              </a>
            </div>
          </transition>
        </div>
      </nav>

      <span v-if="live.enabled" class="shell-live" :class="{ 'is-on': live.connected }" :title="live.connected ? 'Данные обновляются сами (соединение установлено)' : 'Нет соединения для автообновления: данные обновляются по кнопкам и таймерам'">
        <i class="fas fa-circle"></i>
      </span>

      <span v-if="syncStale" class="shell-stale" :title="syncTitle">
        <i class="fas fa-exclamation-triangle"></i>
        <span class="shell-stale__text">{{ syncText }}</span>
      </span>

      <div class="shell-profile">
        <button type="button" class="shell-profile-btn" :title="userName" @click.stop="toggleProfile">
          <span class="shell-user-name">{{ userFirstName }}</span>
          <span class="shell-avatar">{{ userInitial }}</span>
        </button>

        <transition name="dd">
        <div v-show="profileOpen" class="shell-profile-dropdown">
          <div class="shell-profile-info">
            <span class="shell-profile-info__name">{{ userName }}</span>
            <span v-if="currentUser.username && currentUser.username !== userName" class="shell-profile-info__login">{{ currentUser.username }}</span>
          </div>
          <button type="button" class="shell-profile-btn-item" @click.stop="openProfile">
            <i class="fas fa-user-cog"></i>
            Кабинет и уведомления
          </button>
          <button v-if="pwa.canInstall" type="button" class="shell-profile-btn-item" @click.stop="installFromMenu">
            <i class="fas fa-download"></i>
            Установить приложение
          </button>
          <button v-if="canLock" type="button" class="shell-profile-btn-item" @click.stop="doLock">
            <i class="fas fa-lock"></i>
            Заблокировать экран
          </button>
          <div v-if="switchUsers.length" class="shell-switch">
            <span class="shell-switch__title">Войти как</span>
            <div class="shell-switch__list">
              <button v-for="u in switchUsers" :key="u.id" type="button" class="shell-switch__item" :title="u.role || u.name" @click.stop="doSwitchUser(u)">
                <span class="shell-switch__avatar">{{ (u.name || '?').charAt(0).toUpperCase() }}</span>
                <span class="shell-switch__name">{{ u.name }}</span>
                <span v-if="u.role" class="shell-switch__role">{{ u.role }}</span>
              </button>
            </div>
          </div>
          <button type="button" class="shell-logout-btn" @click.stop="doLogout">
            <i class="fas fa-sign-out-alt"></i>
            Выйти
          </button>
        </div>
        </transition>
      </div>
    </header>

    <main class="shell-main">
      <slot />
    </main>

    <!-- Экран блокировки: заставка с часами и ввод ПИН-кода -->
    <lock-screen v-if="lockShown" />

    <!-- Приглашение включить уведомления: всем, кому браузер это позволяет и кто ещё не включил -->
    <push-prompt v-if="!lockShown" />

    <!-- Вышла новая версия: страница обновится сама при переходе или после минуты простоя -->
    <div v-if="version.available" class="shell-update" role="status">
      <span class="shell-update__text"><i class="fas fa-sync-alt"></i><span>Вышла новая версия — страница обновится сама</span></span>
      <button type="button" class="shell-update__btn" @click="reloadNow()">Обновить сейчас</button>
    </div>
  </div>
</template>

<style scoped>
.app-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.shell-header {
  position: relative;
  flex-shrink: 0;
  z-index: var(--z-header, 100);
  display: flex;
  align-items: center;
  gap: var(--space-20);
  padding: var(--space-8) var(--space-20);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-bottom: var(--border-width-default) solid var(--border-default);
  box-shadow: var(--shadow-sm);
}

.shell-logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  line-height: 0;
  transition: opacity var(--duration-normal) var(--ease-base);
}
.shell-logo:hover {
  opacity: 0.75;
}
.shell-logo img {
  height: 2.25rem;
  width: auto;
}

.shell-nav {
  flex: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-4);
  min-width: 0;
}

.shell-nav-item {
  position: relative;
  flex-shrink: 0;
}

.shell-nav-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-8) var(--space-10);
  border-radius: var(--radius-md);
  border: none;
  background: none;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  font-size: var(--text-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  text-decoration: none;
  transition: color var(--duration-normal) var(--ease-base), background var(--duration-normal) var(--ease-base);
}
.shell-nav-link:hover {
  color: var(--primary);
  background: var(--bg-subtle);
}
.shell-nav-link.is-active {
  color: var(--primary);
}
@media (min-width: 901px) {
  /* Полоска под активным разделом выезжает слева направо */
  .shell-nav-link::after {
    content: '';
    position: absolute;
    left: var(--space-10);
    right: var(--space-10);
    bottom: 1px;
    height: 2px;
    border-radius: 2px;
    background: var(--primary);
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform var(--duration-medium) var(--ease-out);
  }
  .shell-nav-link.is-active::after { transform: scaleX(1); }
}

.shell-caret {
  font-size: var(--text-2xs);
  transition: transform var(--duration-normal) var(--ease-base);
}
.shell-nav-item.is-open .shell-caret {
  transform: rotate(180deg);
}

.shell-dropdown {
  position: absolute;
  top: calc(100% + var(--space-5));
  left: 0;
  min-width: 200px;
  width: max-content;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-6);
  background: var(--bg-surface);
  border: var(--border-width-default) solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  z-index: var(--z-dropdown);
}

.shell-dropdown-link {
  transition: background var(--duration-fast) var(--ease-base), color var(--duration-fast) var(--ease-base), padding-left var(--duration-normal) var(--ease-out);
  display: block;
  padding: var(--space-8) var(--space-10);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}
.shell-dropdown-link:hover {
  background: var(--bg-subtle);
  color: var(--primary);
  padding-left: calc(var(--space-10) + 3px);
}
.shell-dropdown-link.is-active {
  color: var(--primary);
  background: var(--primary-soft-2);
}

.shell-stale {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-3) var(--space-8);
  border-radius: var(--radius-pill);
  background: var(--warning-soft);
  color: var(--warning-text);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  cursor: help;
}

.shell-profile {
  position: relative;
  flex-shrink: 0;
}

.shell-profile-btn {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
}

.shell-update {
  position: fixed;
  left: 50%;
  bottom: var(--space-16);
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  max-width: calc(100vw - 32px);
  padding: var(--space-6) var(--space-10);
  font-size: var(--text-sm);
  color: var(--text-primary);
  background: var(--bg-surface);
  border: var(--border-width-default) solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}
.shell-update i { color: var(--primary); }
.shell-update__text { display: flex; align-items: center; gap: var(--space-8); min-width: 0; }
.shell-update__btn {
  flex-shrink: 0;
  padding: var(--space-3) var(--space-8);
  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-brand-800);
  background: var(--primary-soft-2);
  border: 0;
  border-radius: var(--radius-md);
  cursor: pointer;
}
.shell-update__btn:hover { filter: brightness(0.96); }
/* Телефон: плашка во всю ширину, текст и кнопка — построчно */
@media (max-width: 640px) {
  .shell-update { left: var(--space-8); right: var(--space-8); bottom: var(--space-8); transform: none; max-width: none; flex-direction: column; align-items: stretch; gap: var(--space-5); }
  .shell-update__btn { width: 100%; padding: var(--space-5) var(--space-8); }
}
.shell-live { display: inline-flex; align-items: center; color: var(--text-muted); font-size: 9px; cursor: default; }
.shell-live.is-on { color: var(--success-text); }
/* Соединение есть: индикатор мягко светится */
.shell-live.is-on i { animation: shell-live-glow 3s ease-in-out infinite; }
@keyframes shell-live-glow {
  0%, 100% { filter: drop-shadow(0 0 1px currentColor); }
  50% { filter: drop-shadow(0 0 5px currentColor) drop-shadow(0 0 9px currentColor); }
}
@media (prefers-reduced-motion: reduce) {
  .shell-live.is-on i { animation: none; filter: drop-shadow(0 0 3px currentColor); }
}
.shell-user-name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.shell-profile-info {
  display: flex;
  flex-direction: column;
  padding: var(--space-6) var(--space-10) var(--space-8);
  margin-bottom: var(--space-4);
  border-bottom: var(--border-width-default) solid var(--border-default);
}
.shell-profile-info__name {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  word-break: break-word;
}
.shell-profile-info__login {
  font-size: var(--text-xs);
  color: var(--text-tertiary);
}

.shell-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: var(--radius-pill);
  background: var(--primary-soft);
  color: var(--primary);
  font-weight: var(--font-weight-bold);
  font-size: var(--text-base);
}

.shell-profile-dropdown {
  position: absolute;
  top: calc(100% + var(--space-5));
  right: 0;
  min-width: 300px;
  max-width: calc(100vw - 24px);
  padding: var(--space-6);
  background: var(--bg-surface);
  border: var(--border-width-default) solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  z-index: var(--z-dropdown);
}

.shell-profile-btn-item {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  width: 100%;
  padding: var(--space-8) var(--space-10);
  border: none;
  border-radius: var(--radius-md);
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--text-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  text-align: left;
  white-space: nowrap;
}
.shell-profile-btn-item i { width: 1.1em; text-align: center; color: var(--text-tertiary); }
.shell-profile-btn-item:hover { background: var(--bg-subtle); color: var(--primary); }
.shell-profile-btn-item:hover i { color: var(--primary); }

.shell-switch { margin: var(--space-4) 0; padding: var(--space-4) 0; border-top: var(--border-width-default) solid var(--border-default); border-bottom: var(--border-width-default) solid var(--border-default); }
.shell-switch__title { display: block; padding: var(--space-2) var(--space-10); font-size: var(--text-xs); font-weight: var(--font-weight-semibold); color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.04em; }
.shell-switch__list { max-height: 260px; overflow-y: auto; }
.shell-switch__item {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  width: 100%;
  padding: var(--space-4) var(--space-10);
  border: none;
  border-radius: var(--radius-md);
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--text-base);
  color: var(--text-primary);
  text-align: left;
}
.shell-switch__item:hover { background: var(--bg-subtle); color: var(--primary); }
.shell-switch__avatar { display: flex; flex-shrink: 0; align-items: center; justify-content: center; width: 1.7rem; height: 1.7rem; border-radius: var(--radius-pill); background: var(--primary-soft); color: var(--primary); font-size: var(--text-sm); font-weight: var(--font-weight-bold); }
.shell-switch__name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: var(--font-weight-semibold); }
.shell-switch__role { flex-shrink: 0; font-size: var(--text-xs); color: var(--text-tertiary); }

.shell-logout-btn {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  width: 100%;
  padding: var(--space-8) var(--space-10);
  border: none;
  border-radius: var(--radius-md);
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--text-base);
  font-weight: var(--font-weight-semibold);
  color: var(--danger-text);
  text-align: left;
}
.shell-logout-btn:hover {
  background: var(--danger-soft);
}

.shell-main {
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.shell-burger {
  display: none;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border: var(--border-width-default) solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-surface);
  color: var(--text-primary);
  font-size: var(--text-md);
  cursor: pointer;
}
.shell-burger.is-open {
  color: var(--primary);
  border-color: var(--primary);
}

/* Телефон и планшет в портрете: меню сворачивается за кнопкой «бургер», разделы раскрываются списком. */
@media (max-width: 900px) {
  .shell-header {
    gap: var(--space-8);
    padding: calc(var(--space-6) + env(safe-area-inset-top, 0px)) calc(var(--space-10) + env(safe-area-inset-right, 0px)) var(--space-6) calc(var(--space-10) + env(safe-area-inset-left, 0px));
  }
  .shell-logo img { height: 2rem; }
  .shell-burger { display: inline-flex; margin-left: auto; }
  .shell-nav {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: var(--z-dropdown);
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: stretch;
    gap: var(--space-1);
    padding: var(--space-6) var(--space-10) var(--space-10);
    max-height: calc(100vh - 4rem);
    max-height: calc(100dvh - 4rem);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background: var(--bg-surface);
    border-bottom: var(--border-width-default) solid var(--border-default);
    box-shadow: var(--shadow-xl);
  }
  .shell-nav.is-open { display: flex; }
  .shell-nav-item { width: 100%; }
  .shell-nav-link {
    width: 100%;
    justify-content: space-between;
    min-height: 2.75rem;
    padding: var(--space-8) var(--space-10);
    font-size: var(--text-base);
  }
  .shell-dropdown {
    position: static;
    width: auto;
    min-width: 0;
    margin: 0 0 var(--space-4) var(--space-10);
    padding: 0;
    border: none;
    box-shadow: none;
    background: transparent;
  }
  .shell-dropdown-link {
    min-height: 2.5rem;
    display: flex;
    align-items: center;
    white-space: normal;
  }
  .shell-profile-dropdown { right: 0; }
}

@media (max-width: 640px) {
  .shell-header {
    gap: var(--space-8);
  }
  /* На узком экране имя только в подсказке и в выпадающем меню — остаётся круг. */
  .shell-user-name { display: none; }
  /* На узком экране остаётся только значок; подробности — в подсказке. */
  .shell-stale__text { display: none; }
  /* Справа сначала профиль, за ним кнопка меню; индикатор автообновления на телефоне скрыт */
  .shell-profile { margin-left: auto; }
  .shell-burger { order: 1; margin-left: 0; }
  .shell-live { display: none; }
}
</style>

<!-- Не scoped: правила для страниц внутри оболочки на телефоне и планшете в портрете. -->
<style>
@media (max-width: 900px) {
  /* Страница прокручивается целиком (раньше таблица и карточки делили фиксированную высоту, и прокрутки не было). */
  /* без -webkit-overflow-scrolling: в iOS Safari он делает fixed-окна (модальные) частью прокручиваемой страницы: шапка окна с крестиком уезжает вверх */
  .shell-main {
    overscroll-behavior: contain;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
  .shell-main .board,
  .shell-main .main-content,
  .shell-main .page {
    height: auto !important;
    min-height: 0;
    overflow: visible !important;
    flex: none !important;
  }
  .shell-main .board { padding: var(--space-6) var(--space-6) var(--space-12) !important; }
  /* Касание пальцем: всё, что нажимается, не меньше 44 px (рекомендация Apple); мелкие значки и ссылки получают невидимую область нажатия */
  .shell-logo,
  .shell-profile-btn { min-height: 44px !important; min-width: 44px; justify-content: center; }
  .shell-burger { width: 44px !important; height: 44px !important; }
  .cf__control, .sm-select__control, .msel__control, .dtf__date, .dtf__time, .dtf__clear, .pp__input, .pp__arrow, .pp__btn, .dn-input, .dn-field,
  .app-table__sortselect, .app-table__sortdir, .abtn, .seg__btn, .bt-filter__btn, .sh-filter__btn, .dr-filter__btn, .vh-filter__btn,
  .ww-filter__btn, .fuel-filter__btn, .wash-filter__btn, .leg-tab, .vh-chip, .order-form__point, .repair-form__point,
  .order-form__mode, .repair-form__mode, .repair-type__btn, .repair-point, .ap-media, .shell-nav-link, .shell-dropdown-link, .bt-clear,
  .sh-driver__btn, .sh-driver__x, .sh-driver__ok { min-height: 44px !important; }
  .sm-modal__close { flex-shrink: 0; }
  /* финансовый календарь: строки недели и плашки заявок; номер дня в сетке месяца — с невидимой областью нажатия */
  .fw__head, .fm__event { min-height: 44px !important; }
  .fm__num { position: relative; }
  .fm__num::after { content: ''; position: absolute; inset: -12px -10px; }
  .dtf__clear, .pp__arrow, .app-table__sortdir, .sh-driver__x, .sh-driver__ok, .abtn { min-width: 44px; }
  /* галочки с подписью: вся строка нажимается и выше 44 px */
  .gantt-check, .zn-check, .pl-check { display: flex; align-items: center; gap: var(--space-6); min-height: 44px; }
  .gantt-check input, .zn-check input, .pl-check input { width: 22px; height: 22px; flex-shrink: 0; }
  /* цвета партий: крупнее */
  .bt-dot { width: 2.4rem !important; height: 2.4rem !important; }
  .bt-custom { min-width: 2.4rem; min-height: 2.4rem; }
  /* выключатель: высота остаётся прежней, нажимается область 44 px */
  .vh-switch::after { content: ''; position: absolute; inset: -10px 0; }
  /* ссылки-значения в таблицах и карточках: невидимые поля вокруг текста, вёрстка не меняется */
  .dr-car--link, .cell-car--link, .cell-click, .dup-badge--link, .sy-name {
    display: inline-block; padding: 13px 6px; margin: -13px -6px;
  }
  /* Переключатели-фильтры не вылезают за экран: прокручиваются вбок. */
  .shell-main .seg,
  .shell-main .wash-filter,
  .shell-main .repair-type,
  .shell-main .fuel-filter,
  .shell-main .ww-filter,
  .shell-main .dr-filter,
  .shell-main .sh-filter {
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .shell-main .seg__btn,
  .shell-main .wash-filter__btn,
  .shell-main .repair-type__btn,
  .shell-main .fuel-filter__btn,
  .shell-main .ww-filter__btn,
  .shell-main .dr-filter__btn,
  .shell-main .sh-filter__btn { white-space: nowrap; flex-shrink: 0; }
  /* Кнопки и ссылки крупнее под палец */
  .shell-main .abtn { min-height: 2.5rem; }

  /* Страницы «форма + карта» (заказ, маршрут, расчёт плеча): форма в полную высоту без внутренней прокрутки, карта под ней. */
  .shell-main #app,
  .shell-main #shoulder-app {
    height: auto !important;
    min-height: 100%;
  }
  .shell-main .main-layout,
  .shell-main .order-form-layout {
    flex-direction: column !important;
    overflow: visible !important;
    flex: none !important;
    height: auto !important;
  }
  .shell-main .form-panel,
  .shell-main .order-form-card,
  .shell-main .repair-form-card,
  .shell-main .left-panel {
    max-height: none !important;
    overflow: visible !important;
    flex: none !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
  .shell-main .map-panel,
  .shell-main .order-map-panel,
  .shell-main .right-panel {
    flex: none !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    height: 340px !important;
    min-height: 340px !important;
  }
}
</style>
